import { client } from './generated/client.gen'
import { apiHealth } from './health'

/**
 * The one configured API client (scope §9 Contracts: generated from packages/contracts/openapi.json by
 * `pnpm --filter @patchbay/web api:gen`; no hand-written api.ts). Every generated SDK call goes through
 * `apiFetch`, which:
 *  - sends the session cookie (same origin) and echoes the CSRF token in `X-CSRF-Token` on every non-GET (§11);
 *  - records reachability for the "API unreachable" banner (network error/timeout or 502/504 only);
 *  - turns a 401 into one "signed out" signal the shell answers by going to /signin.
 *
 * Screens call SDK functions through `unwrap()`, which throws `ApiError` (the API's `{detail, code}` shape)
 * or `ApiUnreachableError`, so a failed lookup can never be mistaken for an empty answer (P11).
 */

export const CSRF_HEADER = 'X-CSRF-Token'
export const CSRF_COOKIE = 'pb_csrf'
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS'])

let csrfToken: string | null = null

/** The session's CSRF token (from `GET /auth/session` → `me.csrf_token`). The cookie is the fallback. */
export function setCsrfToken(token: string | null): void {
  csrfToken = token
}

export function readCookie(name: string, cookie: string = globalThis.document?.cookie ?? ''): string | null {
  for (const part of cookie.split(';')) {
    const [k, ...v] = part.trim().split('=')
    if (k === name) return decodeURIComponent(v.join('='))
  }
  return null
}

export function currentCsrfToken(): string | null {
  return csrfToken ?? readCookie(CSRF_COOKIE)
}

const unauthListeners = new Set<() => void>()

/** The shell subscribes once: a 401 anywhere means the session is gone. */
export function onUnauthenticated(fn: () => void): () => void {
  unauthListeners.add(fn)
  return () => unauthListeners.delete(fn)
}

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly detail: string
  constructor(status: number, code: string, detail: string) {
    super(detail || code)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.detail = detail
  }
  /** 501 from a W0 stub: the owning slice is not merged yet. */
  get notBuilt(): boolean {
    return this.status === 501
  }
}

export class ApiUnreachableError extends Error {
  constructor(message = 'Patchbay API unreachable') {
    super(message)
    this.name = 'ApiUnreachableError'
  }
}

/** Stable, person-readable copy for API codes that otherwise leak implementation names into the UI. */
export const ERROR_COPY: Readonly<Record<string, string>> = Object.freeze({
  disclosure_risk: "This could expose private service information, so it wasn't sent.",
  file_type: "That file type isn't supported here. Choose a supported file and try again.",
  finish_case_required: 'Use Finish case so Patchbay can check the time and resolution first.',
  not_implemented: 'Not available yet (this part of Patchbay is still being built).',
  offline: 'You are offline. Reconnect and try again.',
  pending_token: 'Connect Desk to continue.',
  role: 'Your role cannot do this.',
  role_forbidden: 'Your role cannot do this.',
  superadmin_required: 'Only the Patchbay superadmin can do this.',
  unauthenticated: 'Your session ended. Sign in again.',
  unreachable: "Patchbay can't reach the server.",
  validation: 'Check the information you entered and try again.',
})

const HTTP_ERROR_COPY: Readonly<Record<number, string>> = Object.freeze({
  401: 'Your session ended. Sign in again.',
  403: "You don't have permission to do that.",
  404: "We couldn't find that item. It may have moved or been removed.",
  409: 'This changed while you were working. Refresh and try again.',
  422: 'Check the information you entered and try again.',
  429: 'Too many tries. Wait a minute and try again.',
  502: "Patchbay can't reach a connected service. Try again.",
  504: "Patchbay can't reach a connected service. Try again.",
})

interface ErrorFacts {
  status?: number
  code?: string
  detail?: string
  message?: string
  name?: string
}

function jsonValue(text: string): unknown {
  const trimmed = text.trim()
  if (!((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']')))) {
    return undefined
  }
  try {
    return JSON.parse(trimmed)
  } catch {
    return undefined
  }
}

/** A JSON error body is unpacked, never returned verbatim to the screen. */
function errorFacts(value: unknown): ErrorFacts {
  if (typeof value === 'string') {
    const parsed = jsonValue(value)
    return parsed === undefined ? { message: value.trim() || undefined } : errorFacts(parsed)
  }
  if (!value || typeof value !== 'object') return {}
  const record = value as Record<string, unknown>
  const message = typeof record.message === 'string' ? record.message.trim() : undefined
  const fromMessage = message ? jsonValue(message) : undefined
  const nested: ErrorFacts = fromMessage === undefined ? {} : errorFacts(fromMessage)
  const detail = typeof record.detail === 'string' ? record.detail.trim() : undefined
  return {
    status: typeof record.status === 'number' && Number.isFinite(record.status) ? record.status : nested.status,
    code: typeof record.code === 'string' && record.code.trim() ? record.code.trim() : nested.code,
    detail: detail && jsonValue(detail) === undefined ? detail : nested.detail,
    message: message && fromMessage === undefined ? message : nested.message,
    name: typeof record.name === 'string' ? record.name : nested.name,
  }
}

function isNetworkError(err: unknown, facts: ErrorFacts): boolean {
  if (err instanceof ApiUnreachableError || facts.status === 0) return true
  if (facts.name === 'TimeoutError') return true
  const words = `${facts.code ?? ''} ${facts.message ?? ''}`
  return /(?:failed to fetch|load failed|network (?:error|request failed)|request timed out|timeout|unreachable)/i.test(words)
}

/** TanStack aborts an older refetch when a newer invalidation supersedes it. That is not an API outage. */
export function isRequestAbort(error: unknown): boolean {
  return Boolean(error && typeof error === 'object' && 'name' in error && error.name === 'AbortError')
}

const GATEWAY = new Set([502, 504])

function waitForSharedResponse(pending: Promise<Response>, signal: AbortSignal | null): Promise<Response> {
  if (!signal) return pending
  if (signal.aborted) return Promise.reject(signal.reason ?? new DOMException('request aborted', 'AbortError'))
  return new Promise((resolve, reject) => {
    const aborted = () => {
      signal.removeEventListener('abort', aborted)
      reject(signal.reason ?? new DOMException('request aborted', 'AbortError'))
    }
    signal.addEventListener('abort', aborted, { once: true })
    pending.then(
      (response) => {
        signal.removeEventListener('abort', aborted)
        resolve(response)
      },
      (error: unknown) => {
        signal.removeEventListener('abort', aborted)
        reject(error)
      },
    )
  })
}

/** Wraps `fetch` for the generated client. Exported for tests. */
export function createApiFetch(base: typeof fetch = (...a) => globalThis.fetch(...a)) {
  const inFlightGets = new Map<string, Promise<Response>>()

  return async (input: Request): Promise<Response> => {
    const req = input
    if (!SAFE_METHODS.has(req.method.toUpperCase())) {
      const token = currentCsrfToken()
      if (token) req.headers.set(CSRF_HEADER, token)
    }
    const execute = async (request: Request): Promise<Response> => {
      let res: Response
      try {
        res = await base(request)
      } catch (err) {
        if (isRequestAbort(err)) throw err
        apiHealth.markDown()
        throw new ApiUnreachableError(err instanceof Error ? err.message : 'network error')
      }
      const apiJson = (res.headers.get('content-type') ?? '').includes('json')
      if (GATEWAY.has(res.status)) {
        apiHealth.markDown()
      } else {
        apiHealth.markUp()
      }
      // API errors have the contract's JSON shape. An HTML 401 is an edge/proxy failure, not proof
      // that the Patchbay session expired.
      if (res.status === 401 && apiJson) for (const fn of [...unauthListeners]) fn()
      return res
    }

    if (req.method.toUpperCase() === 'GET') {
      const headers = [...req.headers.entries()].sort(([left], [right]) => left.localeCompare(right))
      const key = `${req.url}\n${req.credentials}\n${JSON.stringify(headers)}`
      let pending = inFlightGets.get(key)
      if (!pending) {
        // One subscriber cancelling a superseded query must not cancel another subscriber's identical read.
        const sharedRequest = new Request(req, { signal: null })
        pending = execute(sharedRequest).finally(() => inFlightGets.delete(key))
        inFlightGets.set(key, pending)
      }
      return (await waitForSharedResponse(pending, req.signal)).clone()
    }

    return execute(req)
  }
}

/** Idempotent; runs once on import, and tests may call it again with a stub fetch. `baseUrl` stays '' in the browser (same origin, Caddy routes /api). */
export function configureApi(opts: { baseUrl?: string; fetch?: typeof fetch } = {}): void {
  client.setConfig({
    baseUrl: opts.baseUrl ?? '',
    credentials: 'same-origin',
    fetch: createApiFetch(opts.fetch) as typeof fetch,
  })
}

configureApi()

interface SdkResult<T> {
  data?: T
  error?: unknown
  response?: Response
}

function errorFrom(error: unknown, response: Response | undefined): Error {
  if (isRequestAbort(error)) return error as Error
  if (error instanceof ApiUnreachableError) return error
  if (!response) {
    apiHealth.markDown()
    return new ApiUnreachableError(error instanceof Error ? error.message : 'request failed')
  }
  if (GATEWAY.has(response.status)) return new ApiUnreachableError(`API answered ${response.status}`)
  if (!(response.headers.get('content-type') ?? '').includes('json')) {
    return new ApiError(response.status, 'unexpected_response', `API answered ${response.status} with a page, not data`)
  }
  if (error && typeof error === 'object' && 'code' in error) {
    const e = error as { code?: unknown; detail?: unknown; errors?: unknown }
    const detail = typeof e.detail === 'string' ? e.detail : String(e.code)
    return new ApiError(response.status, String(e.code), response.status === 422 ? validationMessage(e.errors, detail) : detail)
  }
  return new ApiError(response.status, 'http_error', typeof error === 'string' ? error.slice(0, 200) : `HTTP ${response.status}`)
}

/**
 * A 422 carries the server's own reason in `errors[0].msg` (pydantic prefixes "Value error, "). Show that, not the bare
 * "validation failed" that hid "wrap-up is its own action" (#994).
 */
export function validationMessage(errors: unknown, fallback: string): string {
  const first = Array.isArray(errors) ? errors[0] : undefined
  const msg = first && typeof first === 'object' && 'msg' in first ? (first as { msg?: unknown }).msg : undefined
  if (typeof msg !== 'string' || !msg.trim()) return fallback
  return msg.replace(/^Value error,\s*/i, '').trim()
}

/** Await an SDK call and return its data, or throw `ApiError` / `ApiUnreachableError`. */
export async function unwrap<T>(call: Promise<SdkResult<T>>): Promise<T> {
  const r = await call
  if (r.error !== undefined) throw errorFrom(r.error, r.response)
  const res = r.response
  if (res && res.status !== 204 && !(res.headers.get('content-type') ?? '').includes('json')) {
    // Vite preview / nginx serve index.html for unknown paths: a page is not an API answer.
    throw new ApiError(res.status, 'unexpected_response', 'The API answered with a page, not data')
  }
  return r.data as T
}

/** One line for a banner or card. API codes and JSON bodies never reach the screen verbatim. */
export function describeError(err: unknown, context?: string): string {
  const facts = errorFacts(err)
  const knownCopy = facts.code ? ERROR_COPY[facts.code] : undefined
  if (knownCopy) return knownCopy
  if (isNetworkError(err, facts)) return "Patchbay can't reach the server."

  const detail = facts.detail && facts.detail !== facts.code ? facts.detail : undefined
  const message = facts.message && facts.message !== facts.code ? facts.message : undefined
  if (detail) return detail
  if (message) return message
  const statusCopy = facts.status ? HTTP_ERROR_COPY[facts.status] : undefined
  if (statusCopy) return statusCopy

  const fallback = context?.trim() || 'Something went wrong'
  return `${fallback}; try again${facts.status ? ` (HTTP ${facts.status})` : ''}`
}
